import Controller from './application_controller'

/**
 * Displays an error message if the Turbo request fails after the following events,
 * provided they were not handled with preventDefault()
 *
 * Error Types:
 *   turbo:error - Fired for all after specific error events, if they were not handled with preventDefault()
 *     \_ turbo:network-error - Client network connection error
 *     \_ turbo:gateway-error - 502, 503, 504 Gateway errors
 *     \_ turbo:server-error - 500 Internal Server Error
 */
export default class extends Controller {
  static targets = ['networkError', 'gatewayError', 'serverError']

  types = ['network-error', 'gateway-error', 'server-error']

  closeTimeout = null

  connect() {
    this.addEventListener('turbo:fetch-request-error', event => this.handleError('network-error', event))
    this.addEventListener('turbo:before-fetch-response', event => this.errorTargetForType('network-error')?.close())
    this.addEventListener('turbo:before-fetch-response', event => this.handleResponseError(event))
    this.addEventListener('turbo:frame-missing', event => this.handleFrameMissing(event))
    this.addEventListener('turbo:before-cache', event => this.close())

    this.close()
  }

  disconnect() {
    clearTimeout(this.closeTimeout)
    super.disconnect()
  }

  handleError(type, event, detail = {}) {
    if (this.isPrefetchRequest(event)) {
      return
    }

    if (!this.willHandleError(type, event, detail)) {
      clearTimeout(this.closeTimeout)
      this.showError(type)
      event.preventDefault()
    }
  }

  handleResponseError(event) {
    const status = event.detail.fetchResponse.response.status
    if (status === 500) {
      this.handleError('server-error', event, { status })
    } else if (status > 500) {
      this.handleError('gateway-error', event, { status })
    } else if (status >= 200 && status < 400) {
      this.shouldCloseAfterSuccessfulResponse()
    }
  }

  handleFrameMissing(event) {
    this.handleError('server-error', event, { status: 404 })
  }

  shouldCloseAfterSuccessfulResponse() {
    clearTimeout(this.closeTimeout)
    this.closeTimeout = setTimeout(() => this.close(), 6000)
  }

  showError(type) {
    const alert = this.errorTargetForType(type)
    if (!alert) return

    if (alert.open) {
      alert.style.animation = 'none'
      void alert.offsetWidth
      alert.style.animation = null
    } else {
      alert.show()
    }
  }

  close() {
    this.types.forEach(type => this.errorTargetForType(type)?.close())
  }

  // Fires a more semantic event that can be handled elsewhere.
  // If handled, event.preventDefault() should be called, and this controller
  // will not display an error message.
  willHandleError(type, event, detail = {}) {
    return [type, 'error'].find(eventName => this.dispatch(eventName, {
      prefix: 'turbo',
      target: event.target,
      detail: { errorEvent: event, ...detail },
      bubbles: true,
      cancelable: true
    }).defaultPrevented)
  }

  isPrefetchRequest(event) {
    return !event.target.matches('html, turbo-frame, form')
  }

  errorTargetForType(type) {
    switch (type) {
      case 'network-error':
        if (this.hasNetworkErrorTarget) {
          return this.networkErrorTarget
        } else {
          return null
        }
      case 'gateway-error':
        if (this.hasGatewayErrorTarget) {
          return this.gatewayErrorTarget
        } else {
          return null
        }
      case 'server-error':
        if (this.hasServerErrorTarget) {
          return this.serverErrorTarget
        } else {
          return null
        }

      default:
        throw new Error(`Unknown error type: ${type}`)
    }
  }
}
